// Today's smaller sections, as AdminToday.dc.html draws them: Live today (the black card), New this
// week, Coming up, Going quiet and Access ending. Each reads its own hub.today_* read (10.4.2) and
// draws its empty state when it has nothing; the words are words.js's. A mastermind's member opens
// in Masterminds for owner and admin (memberHref; the Accelerator critique's admin 5, 20 Oct), and a
// mastermind group's session on Live today counts its register from the group (mastermindJobs.js).
import { Link } from '../../lib/router.jsx'
import { Dot, Loading, Problem, productDot } from '../ui/index.js'
import {
  scheduleLines, liveLines, dateBlockOf, freshWhat, quietWhy, endingWhy, dayWithName, typed, memberHref,
} from './words.js'
import { registerCount } from './mastermindJobs.js'

// The dot beside a schedule row or a new member: the product's own (the board's), the 1-2-1's ink, and
// a free webinar's white with its ring
export function rowDot(row, products = []) {
  if (row.kind === 'client_session') return 'var(--shell-ink)'
  if (row.kind === 'webinar') return 'var(--shell-bg)'
  return productDot(typed(row, products))
}

// The black card at the top: what's on today. 1 per session, 1-2-1 or webinar today; nothing when
// there's nothing on. mm: mastermindJobs' answer (owner and admin), for a group's register count.
export function LiveToday({ items, nowMs, products = [], mm = null }) {
  if (!items?.length) return null
  return items.map(s => {
    const mastermind = s.kind === 'session' && (products || []).find(p => p.id === s.product_id)?.type === 'mastermind'
    const lines = liveLines(s, nowMs, { mastermind, register: mastermind ? registerCount(mm, s.item_id) : null })
    return (
      <section key={`${s.kind}:${s.item_id}`} className="td-live" aria-label="Live today">
        <div className="td-live-text">
          <span className="td-live-label"><span className="td-live-dot" style={{ background: rowDot(s) }} aria-hidden="true" />Live today</span>
          <span className="td-live-title">{lines.title}</span>
          <span className="td-live-detail">{lines.detail}</span>
        </div>
        {lines.link && <Link to={lines.link.to} className="td-live-btn">{lines.link.label}</Link>}
      </section>
    )
  })
}

// a place secured isn't a member of a mastermind yet: its row opens the person page
const freshHref = (f, products, role) => (f.kind === 'access' && f.status !== 'secured'
  ? memberHref(f, products, role) : `/admin/people/${f.person_id}`)

export function NewThisWeek({ load, amounts, products, nowMs, role }) {
  return (
    <section className="td-card" aria-labelledby="td-new-h">
      <div className="td-card-head"><h2 className="td-h2" id="td-new-h">New this week</h2></div>
      {load.status === 'loading' && !load.data && <Loading />}
      {load.status === 'error' && !load.data && <div className="td-pad"><Problem error={load.error} onRetry={load.reload} /></div>}
      {load.data && !load.data.length && <p className="td-empty">Nobody new in the last 7 days.</p>}
      {load.data?.length > 0 && (
        <ul className="td-new" aria-label="New this week">
          {load.data.map(f => (
            <li key={`${f.kind}:${f.access_id || f.webinar_id}:${f.person_id}`} className="td-new-row">
              <span className="td-new-who">
                <Link to={freshHref(f, products, role)} className="td-new-name">{f.full_name}</Link>
                <span className="td-new-email">{f.email}</span>
              </span>
              <span className="td-new-what"><Dot colour={rowDot(f, products)} ringed />{freshWhat(f, amounts, products)}</span>
              <span className="td-new-when">{dayWithName(f.happened_at, nowMs)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function ComingUp({ load, nowMs }) {
  const rows = (load.data || []).filter(s => !s.is_today)
  return (
    <section className="td-card td-side" aria-labelledby="td-coming-h">
      <h2 className="td-h2 td-h2--side" id="td-coming-h">Coming up</h2>
      {load.status === 'loading' && !load.data && <Loading className="td-flush" />}
      {load.status === 'error' && !load.data && <Problem error={load.error} onRetry={load.reload} />}
      {load.data && !rows.length && <p className="td-side-line">Nothing booked in the next 4 weeks.</p>}
      {rows.length > 0 && (
        <ul className="td-coming" aria-label="Coming up">
          {rows.map(s => {
            const b = dateBlockOf(s)
            const lines = scheduleLines(s)
            return (
              <li key={`${s.kind}:${s.item_id}`} className="td-coming-row">
                <span className="td-date" aria-label={dayWithName(s.starts_at, nowMs)}>
                  <span className="td-date-dow" aria-hidden="true">{b.dow}</span>
                  <span className="td-date-day" aria-hidden="true">{b.day}</span>
                  <span className="td-date-mon" aria-hidden="true">{b.mon}</span>
                </span>
                <span className="td-coming-text">
                  <span className="td-coming-title"><Dot colour={rowDot(s)} ringed />{lines.title}</span>
                  <span className="td-coming-detail">{lines.detail}</span>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

export function GoingQuiet({ load, nowMs, products, role }) {
  return (
    <section className="td-card td-side" aria-labelledby="td-quiet-h">
      <h2 className="td-h2 td-h2--side" id="td-quiet-h">Going quiet</h2>
      <span className="td-side-rule">Not logged in for 21 days, or nothing ticked since the last session.</span>
      {load.status === 'loading' && !load.data && <Loading className="td-flush" />}
      {load.status === 'error' && !load.data && <Problem error={load.error} onRetry={load.reload} />}
      {load.data && !load.data.length && <p className="td-side-line">Nobody has gone quiet.</p>}
      {load.data?.length > 0 && (
        <ul className="td-list" aria-label="Going quiet">
          {load.data.map(q => (
            <li key={`${q.person_id}:${q.product_id}`} className="td-list-row">
              <Link to={memberHref(q, products, role)} className="td-list-name">{q.full_name}</Link>
              <span className="td-list-why">{quietWhy(q, nowMs)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function AccessEnding({ load, nowMs, products, role }) {
  return (
    <section className="td-card td-side td-side--tight" aria-labelledby="td-ending-h">
      <h2 className="td-h2 td-h2--side" id="td-ending-h">Access ending</h2>
      {load.status === 'loading' && !load.data && <Loading className="td-flush" />}
      {load.status === 'error' && !load.data && <Problem error={load.error} onRetry={load.reload} />}
      {load.data && !load.data.length && <span className="td-side-line td-side-line--body">Nobody's access ends in the next 30 days.</span>}
      {load.data?.length > 0 && (
        <ul className="td-list" aria-label="Access ending">
          {load.data.map(e => (
            <li key={e.access_id} className="td-list-row">
              <Link to={memberHref(e, products, role)} className="td-list-name">{e.full_name}</Link>
              <span className="td-list-why">{endingWhy(e, nowMs, products)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
